/* eslint-disable */
import { API_URL } from '../config';
import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext(null);

const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes
const LAST_ACTIVITY_KEY = 'lastActivityAt';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const inactivityTimer = useRef(null);

  const markActivity = () => {
    localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
  };

  // Load user from storage on mount, then refresh from server
  useEffect(() => {
    const savedUser = localStorage.getItem('user');
    const token = localStorage.getItem('token');
    if (savedUser && token) {
      try {
        const parts = token.split('.');
        const payload = JSON.parse(atob(parts[1]));
        if (payload.exp && Date.now() >= payload.exp * 1000) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem(LAST_ACTIVITY_KEY);
          setLoading(false);
          return;
        }
        // BUG FIX: the old code only ever checked JWT expiry (7 days) on
        // resume/reload — never actual inactivity duration. A laptop closed
        // overnight would pass this check every time even after 10+ hours
        // idle, since setTimeout-based timers don't survive sleep/background
        // throttling. Check wall-clock elapsed time against the last
        // recorded activity here too, on every page load.
        const lastActivity = parseInt(localStorage.getItem(LAST_ACTIVITY_KEY) || '0', 10);
        if (lastActivity && Date.now() - lastActivity >= INACTIVITY_TIMEOUT) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem(LAST_ACTIVITY_KEY);
          setLoading(false);
          return;
        }
        // Set from localStorage first (fast)
        const localUser = JSON.parse(savedUser);
        setUser(localUser);
        markActivity();
        // Then refresh from server to get latest org name, role etc
        const BASE = API_URL;
        fetch(`${BASE}/api/auth/me`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }).then(r => r.ok ? r.json() : null).then(freshUser => {
          if (freshUser) {
            const updated = { ...localUser, ...freshUser };
            localStorage.setItem('user', JSON.stringify(updated));
            setUser(updated);
          }
        }).catch(() => {}).finally(() => setLoading(false));
        return;
      } catch (e) {
        setUser(JSON.parse(savedUser));
      }
    }
    setLoading(false);
  }, []);

  const performLogout = useCallback(async (reason = '') => {
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    try { await authAPI.logout(); } catch (e) {}
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem(LAST_ACTIVITY_KEY);
    setUser(null);
    if (reason === 'inactivity') {
      window.location.href = '/login?reason=inactivity';
    } else if (reason === 'expired') {
      window.location.href = '/login?reason=expired';
    }
  }, []);

  // BUG FIX: This used to just re-arm a fresh 30-minute setTimeout on every
  // activity event and otherwise trust it blindly. That timer is unreliable
  // across tab backgrounding / system sleep — browsers throttle or fully
  // suspend it, so it silently never fires while the laptop is asleep. It's
  // kept here as a nice-to-have for the "tab stays open and visible" case,
  // but the real enforcement now happens in checkInactivityOnResume, which
  // does actual wall-clock math whenever the user comes back.
  const resetInactivityTimer = useCallback(() => {
    markActivity();
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    inactivityTimer.current = setTimeout(() => {
      performLogout('inactivity');
    }, INACTIVITY_TIMEOUT);
  }, [performLogout]);

  // BUG FIX: This is the actual fix for "left it overnight, still logged
  // in". Rather than trusting a background timer to have fired, this
  // calculates real elapsed time since the last recorded activity whenever
  // the user returns to the tab (visibility change, window focus). This is
  // immune to sleep/throttling since it's just arithmetic done at the
  // moment of return, not something that had to keep running while away.
  const checkInactivityOnResume = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) { performLogout(); return; }
    try {
      const parts = token.split('.');
      const payload = JSON.parse(atob(parts[1]));
      if (payload.exp && Date.now() >= payload.exp * 1000) {
        performLogout('expired');
        return;
      }
    } catch (e) {}

    const lastActivity = parseInt(localStorage.getItem(LAST_ACTIVITY_KEY) || '0', 10);
    if (lastActivity && Date.now() - lastActivity >= INACTIVITY_TIMEOUT) {
      performLogout('inactivity');
    } else {
      // Still within the window — re-arm the visible-tab timer for the
      // REMAINING time, not a fresh full 30 minutes, so it stays accurate.
      resetInactivityTimer();
    }
  }, [performLogout, resetInactivityTimer]);

  // Set up activity listeners and inactivity/expiry checks when user is logged in
  useEffect(() => {
    if (!user) return;

    // Start inactivity timer (visible-tab case)
    resetInactivityTimer();

    // Listen for user activity
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(e => window.addEventListener(e, resetInactivityTimer, { passive: true }));

    // BUG FIX: Check real elapsed time (not just token expiry) whenever the
    // user returns to the tab — covers laptop sleep, screen lock, and
    // backgrounded/minimised windows, all of which silently break
    // setTimeout-based timers alone.
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') checkInactivityOnResume();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', checkInactivityOnResume);

    return () => {
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
      events.forEach(e => window.removeEventListener(e, resetInactivityTimer));
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', checkInactivityOnResume);
    };
  }, [user, resetInactivityTimer, checkInactivityOnResume]);

  const login = async (username, password, organizationName) => {
    // If token already set by Login.js direct fetch, just sync state
    const existingToken = localStorage.getItem('token');
    const existingUser = localStorage.getItem('user');
    if (existingToken && existingUser) {
      const userData = JSON.parse(existingUser);
      setUser(userData);
      markActivity();
      return { token: existingToken, user: userData };
    }
    const data = await authAPI.login(username, password);
    localStorage.setItem('token', data.token);
    localStorage.setItem('user', JSON.stringify(data.user));
    markActivity();
    setUser(data.user);
    return data;
  };

  const getOrganizationId = () => user?.organizationId || 1;

  const logout = async () => {
    await performLogout();
  };

  const isAdmin = () => {
    return !!user?.isSuperAdmin || user?.role === 'owner' || user?.role === 'admin';
  };

  const isOwner = () => {
    return !!user?.isSuperAdmin || user?.role === 'owner';
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, isAdmin, isOwner, getOrganizationId }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
